/**
 * Utility functions for managing user permissions
 */

import { doc, getDoc, setDoc } from "firebase/firestore";
import { firestore } from "../configs/FirebaseConfig";
import navigationConfig from "../configs/NavigationConfig";
import roles from "../roles";

// Extract all permission keys from navigation config
export const extractPermissionKeys = () => {
  const permissionKeys = [];

  navigationConfig.forEach((menu) => {
    if (menu.submenu && menu.submenu.length > 0) {
      menu.submenu.forEach((subMenu) => {
        if (subMenu.submenu && subMenu.submenu.length > 0) {
          // Third level items
          subMenu.submenu.forEach((subSubMenu) => {
            permissionKeys.push(subSubMenu.key);
          });
        } else {
          // Second level items
          permissionKeys.push(subMenu.key);
        }
      });
    } else {
      // Top level items
      permissionKeys.push(menu.key);
    }
  });

  return permissionKeys;
};

export const getPermissionStructure = () => {
  const structure = [];

  navigationConfig.forEach((menu) => {
    if (menu.submenu && menu.submenu.length > 0) {
      menu.submenu.forEach((subMenu) => {
        const group = {
          key: subMenu.key,
          title: subMenu.title,
          hasPath: !!subMenu.path, // Bepaalt of deze parent zelf checkboxes krijgt
          children: [],
        };

        if (subMenu.submenu && subMenu.submenu.length > 0) {
          // Third level items
          subMenu.submenu.forEach((subSubMenu) => {
            group.children.push({
              key: subSubMenu.key,
              title: subSubMenu.title,
            });
          });
        }

        structure.push(group);
      });
    }
  });

  return structure;
};

// Get readable permission titles from navigation config
export const getPermissionTitles = () => {
  const permissionTitles = {};

  const processMenu = (menu, parentTitle = null, categoryTitle = null) => {
    if (menu.submenu && menu.submenu.length > 0) {
      menu.submenu.forEach((subMenu) => {
        if (subMenu.submenu && subMenu.submenu.length > 0) {
          // Third level items
          subMenu.submenu.forEach((subSubMenu) => {
            const title =
              parentTitle && subMenu.title !== menu.title ? `${subMenu.title}/${subSubMenu.title}` : subSubMenu.title;
            permissionTitles[subSubMenu.key] = title;
          });
        } else {
          // Second level items
          const title = parentTitle && subMenu.title !== menu.title ? `${menu.title}/${subMenu.title}` : subMenu.title;
          permissionTitles[subMenu.key] = title;
        }
      });
    } else {
      // Top level items
      permissionTitles[menu.key] = menu.title;
    }
  };

  navigationConfig.forEach((menu) => {
    processMenu(menu);
  });

  return permissionTitles;
};

// Sync admin user permissions with current navigation config
export const syncUserPermissions = async (userId, currentPermissions = {}) => {
  const currentKeys = extractPermissionKeys();
  const existingKeys = Object.keys(currentPermissions);

  let needsUpdate = false;
  const updatedPermissions = { ...currentPermissions };

  // Add missing keys with default values (false for admins)
  currentKeys.forEach((key) => {
    if (!existingKeys.includes(key)) {
      updatedPermissions[key] = {
        view: false,
        edit: false,
      };
      needsUpdate = true;
    }
  });

  // Optionally remove keys that no longer exist in navigation
  // (commented out to preserve historical permissions)
  /*
  existingKeys.forEach(key => {
    if (!currentKeys.includes(key)) {
      delete updatedPermissions[key];
      needsUpdate = true;
    }
  });
  */

  // Update firestore if changes were made
  if (needsUpdate) {
    const { FIRESTORE_USER_PERMISSIONS_TABLE } = await import("../constants/FirestoreConstant");
    const userPermissionsDocRef = doc(firestore, FIRESTORE_USER_PERMISSIONS_TABLE, userId);
    await setDoc(userPermissionsDocRef, updatedPermissions);
    console.log(`Updated permissions for user ${userId}`);
  }

  return updatedPermissions;
};

// Get user permissions with automatic sync for admin users
export const getUserPermissions = async (userId, userRole) => {
  // SuperAdmins don't need permission documents
  if (userRole === roles.superAdmin) {
    return null;
  }

  const { FIRESTORE_USER_PERMISSIONS_TABLE } = await import("../constants/FirestoreConstant");

  try {
    const userPermissionsDocRef = doc(firestore, FIRESTORE_USER_PERMISSIONS_TABLE, userId);
    const userPermissionsDoc = await getDoc(userPermissionsDocRef);

    if (userPermissionsDoc.exists()) {
      const permissions = userPermissionsDoc.data();

      // For admin users, sync with current navigation config
      if (userRole === roles.admin) {
        return await syncUserPermissions(userId, permissions);
      }

      return permissions;
    } else {
      // If no document exists and user is admin, create default permissions
      if (userRole === roles.admin) {
        return await syncUserPermissions(userId, {});
      }
    }
  } catch (error) {
    console.error("Error getting user permissions:", error);
    return {};
  }

  return {};
};

// Save user permissions
export const saveUserPermissions = async (userId, permissions) => {
  const { FIRESTORE_USER_PERMISSIONS_TABLE } = await import("../constants/FirestoreConstant");

  try {
    const userPermissionsDocRef = doc(firestore, FIRESTORE_USER_PERMISSIONS_TABLE, userId);
    await setDoc(userPermissionsDocRef, permissions);
    return true;
  } catch (error) {
    console.error("Error saving user permissions:", error);
    return false;
  }
};

// Get active permissions for display (only permissions set to true)
export const getActivePermissions = (permissions) => {
  if (!permissions) return [];

  const activePermissions = [];
  const permissionTitles = getPermissionTitles();

  Object.keys(permissions).forEach((key) => {
    const permission = permissions[key];
    if (permission.view || permission.edit) {
      activePermissions.push({
        key,
        title: permissionTitles[key] || key,
        hasView: permission.view,
        hasEdit: permission.edit,
      });
    }
  });

  return activePermissions;
};
